import * as _ from 'lodash';
import * as hash from 'object-hash';
import * as moment from 'moment'
import React, { useContext, useEffect, useReducer, useState } from 'react';
import { connect, Dispatch } from 'react-redux';
import { IApplicationState, AppActionCreators, RecipientActionCreators, IRecipientContactInfoResponse, IAddressValidationResponse } from '@nkr/store/index';
import { IBaseResponse, IRecipientContactInformation } from '@nkr/models/index';
import { AddressValidationResult } from '@nkr/enums/address-validation-result';
import { ProfileWrapper } from '../styles';
import { AfterAction, RecipientProfileContext, ToggleType } from '../models';
import { contactInfoReducer, initialState } from './state';
import { toast } from 'react-toastify';
import { ILookupData } from '../lookup-data';
import Content from './contact-info';
import AddressValidation from '@nkr/components/address-validation';
import DuplicateRecordModal from '@nkr/components/duplicate-record-modal';

interface IProps {
    saveRef: React.MutableRefObject<any>,
    lookupData: ILookupData
}

interface IStateProps { }

interface IDispatchProps {
    showLoading: (message?: string) => Promise<void>,
    hideLoading: () => void,
    validateAddress: (centerId: number, recipientId: number, address: string, address2: string, city: string, state: string, postalCode: string) => Promise<IAddressValidationResponse>,
    getContactInfoData: (centerId: number, recipientId: number) => Promise<IRecipientContactInfoResponse>,
    updateContactInfoData: (centerId: number, recipientId: number, recipientContactInfo: IRecipientContactInformation) => Promise<IRecipientContactInfoResponse>,
    orderSwabKit: (recipientId: number, recipientContactInfo: IRecipientContactInformation) => Promise<IBaseResponse>
}

type TProps = IProps & IStateProps & IDispatchProps;

const ContactInformation = (props: TProps) => {
    const context = useContext(RecipientProfileContext);
    const [localState, dispatch] = useReducer(contactInfoReducer, initialState);
    const [showDuplicateRecord, setShowDuplicateRecord] = useState(false);
    const [duplicateMessage, setDuplicateMessage] = useState<string>('');

    useEffect(() => {
        getData();
    }, []);

    // apiPut resolves with the axios error instead of rejecting, so a 409 arrives in .then
    // rather than .catch. BM-9682.
    const handleDuplicateError = (error: any): boolean => {
        if (error?.response?.status != 409) {
            return false;
        }

        setDuplicateMessage(error.response.data);
        setShowDuplicateRecord(true);

        return true;
    };

    const handleCloseDuplicateRecord = () => {
        setShowDuplicateRecord(false);
    };

    const getData = async () => {
        props.showLoading("Getting contact info, one moment please....").then(() => {
            props.getContactInfoData(context.centerId, context.recipientId)
                .then(response => {
                    if (response.data.success) {
                        let data = response.data.results;

                        dispatch({
                            type: 'afterLoad',
                            payload: data
                        });
                    }
                }).catch(() => {

                }).finally(() => {
                    props.hideLoading();
                });
        });
    };

    const save = () => {
        props.showLoading('Saving address, one moment please...').then(() => {
            props.updateContactInfoData(context.centerId, context.recipientId, localState.data)
                .then(response => {
                    if (handleDuplicateError(response)) {
                        return;
                    }

                    if (response.data.success) {
                        toast.success('Address updated successfully');

                        context.updateHasChanges(false);

                        dispatch({
                            type: 'updateHash',
                            payload: hash(localState.data)
                        });
                    }
                })
                .finally(() => {
                    props.hideLoading();
                });
        });
    };

    const validateAddress = async () => {
        let { address, address2, city, state, postalCode } = localState.data;

        if (address && city && state && postalCode) {
            let addressValidationResponse = await props.validateAddress(
                context.centerId,
                context.recipientId,
                address,
                address2,
                city,
                state,
                postalCode
            );

            if (addressValidationResponse.data.success) {
                return addressValidationResponse.data.results;
            }
        }

        return null;
    };

    const validate = () => {
        let birthDateValid = true;
        let birthDate = moment(localState.data.birthDate, 'MM/DD/YYYY');
        let currentYear = moment().year();

        if (birthDate.isValid()) {
            let age = currentYear - birthDate.year();

            if (age < 0 || age > 100) {
                birthDateValid = false;
            }
        } else {
            birthDateValid = false;
        }

        dispatch({
            type: 'updateBirthDateValid',
            payload: birthDateValid
        });

        return birthDateValid;
    }

    const handleDataChange = (data: IRecipientContactInformation) => {
        let newHash = hash(data);

        var hasChanged = localState.hash != newHash;

        dispatch({
            type: 'updateData',
            payload: data
        });

        context.updateHasChanges(hasChanged);
    }

    const handleCheckAddress = async () => {
        let { address, address2, city, state, postalCode } = localState.data;

        props.showLoading('Validating address, one moment please...')
            .then(async () => {
                let addressValidationResponse = await validateAddress();

                let newAddressValidation = {
                    show: addressValidationResponse.result != AddressValidationResult.Valid,
                    response: addressValidationResponse,
                    submittedAddress: {
                        street: address,
                        street2: address2,
                        city: city,
                        state: state,
                        postalCode: postalCode
                    }
                }

                dispatch({
                    type: 'addressValidation',
                    payload: newAddressValidation
                });

                if (addressValidationResponse.result == AddressValidationResult.Valid) {
                    save();
                }
            })
            .finally(() => {
                props.hideLoading()
            });
    }

    const handleUseAddress = (recommended: boolean) => {
        let { data, addressValidation } = localState;

        let { address, address2, city, state, postalCode } = data;

        if (recommended) {
            address = addressValidation.response.address1;
            address2 = addressValidation.response.address2;
            city = addressValidation.response.city;
            state = addressValidation.response.state;
            postalCode = addressValidation.response.zip5;
        }

        data.address = address;
        data.address2 = address2;
        data.city = city;
        data.state = state;
        data.postalCode = postalCode;

        dispatch({
            type: 'useAddress',
            payload: {
                data,
                addressValidation: {
                    show: false,
                    response: null,
                    submittedAddress: null,
                }
            }
        });

        save();
    }

    const handleCancelAddressValidation = () => {
        dispatch({
            type: 'addressValidation',
            payload: {
                show: false,
                response: null,
                submittedAddress: null
            }
        });
    }

    const handleValidate = (afterAction: AfterAction) => {
        let birthDateValid = validate();

        if (birthDateValid) {
            context.updateErrors(ToggleType.NoMessage, []);

            let { address, address2, city, state, postalCode } = localState.data;
            let { address: originalAddress, address2: originalAddress2, city: originalCity, state: originalState, postalCode: originalPostalCode } = localState.originalData;

            let addressPopulated = address && city && state && postalCode;
            let addressChanged = address != originalAddress || address2 != originalAddress2 || city != originalCity || state != originalState || postalCode != originalPostalCode;

            if (addressPopulated && addressChanged) {
                handleCheckAddress();
            } else {
                save();
            }
        } else {
            context.updateErrors(ToggleType.Errors, ['Please specify a valid birth date']);
        }
    };

    context.registerSave(props.saveRef, handleValidate);

    if (localState.data == null || context.headerInfo == null)
        return null;

    return (
        <>
            <ProfileWrapper>
                <Content
                    centerId={context.centerId}
                    recipientId={context.recipientId}
                    stateList={props.lookupData.stateList}
                    data={localState.data}
                    birthDateValid={localState.birthDateValid}
                    onChange={handleDataChange}
                />
            </ProfileWrapper>

            <AddressValidation
                show={localState.addressValidation.show}
                response={localState.addressValidation.response}
                submittedAddresss={localState.addressValidation.submittedAddress}
                onUseAddress={handleUseAddress}
                onCancel={handleCancelAddressValidation}
            />

            <DuplicateRecordModal
                show={showDuplicateRecord}
                message={duplicateMessage}
                onClose={handleCloseDuplicateRecord}
            />
        </>
    );
}


const mapStateToProps = (appState: IApplicationState): IStateProps => {
    return {}
}

const mapDispatchToProps = (dispatch: Dispatch<IApplicationState>): IDispatchProps => {
    return {
        showLoading: (message) => {
            return dispatch(AppActionCreators.showLoadingAsync(message));
        },
        hideLoading: () => {
            dispatch(AppActionCreators.hideLoading());
        },
        validateAddress: (centerId, recipientId, address, address2, city, state, postalCode) => {
            return dispatch(RecipientActionCreators.validateAddress(centerId, recipientId, address, address2, city, state, postalCode));
        },
        getContactInfoData: (centerId, recipientId) => {
            return dispatch(RecipientActionCreators.getContactInfo(centerId, recipientId))
        },
        updateContactInfoData: (centerId, recipientId, contactInfo) => {
            return dispatch(RecipientActionCreators.updateContactInfo(centerId, recipientId, contactInfo));
        },
        orderSwabKit: (recipientId, contactInfo) => {
            return dispatch(RecipientActionCreators.orderSwabKit(recipientId, contactInfo));
        }
    }
}

export default connect<IStateProps, {}, IProps>(
    mapStateToProps,
    mapDispatchToProps)(ContactInformation);
